import { Box, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Button, Checkbox, Chip, IconButton, Avatar, Paper, TablePagination, CircularProgress, Skeleton } from "@mui/material";
import { useContext, useEffect } from "react";
import { CONTEXTS } from "figshelf-modules";
import { WorkspacePremium as WorkspacePremiumIcon, MoreVert as MoreVertIcon } from "@mui/icons-material";
import Tooltip from '@mui/material/Tooltip';

const UserListTablePagination = ({ list, totalItems, rowsPerPage, page, onPageChange, onRowsPerPageChange, loading = false, onMenuOpen }) => {
  const api = useContext(CONTEXTS.APIContext);

  const handlePageChange = (event, newPage) => {
    onPageChange(event, newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onRowsPerPageChange(event);
  };

  useEffect(() => {
    // Array of User Model Type 
  }, [list]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    });
  };

  const getRoleColor = (role) => {
    const roleLower = role?.toLowerCase();
    switch (roleLower) {
      case 'admin': return 'error';
      case 'moderator': return 'warning';
      case 'user': return 'default';
      default: return 'default';
    }
  };

  const getRoleLabel = (role) => {
    if (!role) return 'User';
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'active': return 'success';
      case 'blocked': 
      case 'suspended': return 'error';
      case 'inactive': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    if (!status) return 'Active';
    if (status.toLowerCase() === 'blocked') return 'Suspended';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  return (
    <Box>
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox"><Checkbox /></TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Premium</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (!list || list.length === 0) ? (
                  Array.from({ length: Math.min(rowsPerPage || 6, 12) }).map((_, idx) => (
                    <TableRow key={idx}>
                      <TableCell padding="checkbox"><Checkbox disabled /></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Skeleton variant="circular" width={40} height={40} />
                          <Box sx={{ flex: 1 }}>
                            <Skeleton width="60%" height={18} />
                            <Skeleton width="45%" height={16} />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell><Skeleton width="70%" height={16} /></TableCell>
                      <TableCell><Skeleton width={70} height={22} /></TableCell>
                      <TableCell><Skeleton width={80} height={22} /></TableCell>
                      <TableCell><Skeleton width={40} height={22} /></TableCell>
                      <TableCell>
                        <IconButton size="small" disabled>
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : list && list.length > 0 ? (
                  list.map((user, idx) => (
                    <TableRow key={user.id || idx}>
                      <TableCell padding="checkbox"><Checkbox /></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar src={user.getAvatar ? user.getAvatar() : user.avatar} alt={user.getDisplayName ? user.getDisplayName() : user.name} />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {user.getDisplayName ? user.getDisplayName() : user.name || user.username}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.email || ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.phoneNumber || user.phone || ''}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(user.profile?.createdAt)}</TableCell>
                      <TableCell>
                        <Chip 
                          label={getRoleLabel(user.role)} 
                          color={getRoleColor(user.role)} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getStatusLabel(user.status)} 
                          color={getStatusColor(user.status)} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>
                        {user.isPremium
                          ? <Tooltip title="Supporter"><WorkspacePremiumIcon fontSize="small" sx={{ color: '#FFD700', verticalAlign: 'middle' }} /></Tooltip>
                          : <Typography variant="caption" color="text.disabled">—</Typography>
                        }
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={(e) => onMenuOpen && onMenuOpen(e, user)}>
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No users found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[6, 12, 24]}
            component="div"
            count={totalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserListTablePagination;